import {
  hotelAmenityViewsOf,
  hotelOfferViewOf,
  hotelOfferViewsOf,
  hotelTravelLabelsOf,
  type HotelAmenityView,
  type HotelAxis,
  type HotelOfferFacts,
  type HotelOfferView,
  type HotelOfferViewCopy,
} from '@voll-intelligence/intelligence-ui-kit';
import { fmtMoneyWhole } from '../../shared/ui/flight_format.js';
import { genUiT, resolveGenUiLocale } from '../../shared/ui/i18n.js';
import {
  type ChatUiHotelOffer,
  type HotelPlannerPlace,
  type ChatUiHotelAmenity,
  hotelDistanceKm,
  hotelTravelEstimate,
} from '@voll-intelligence/genui-contract';
import { hotelPolicyReasons } from './hotel_policy_reasons.js';

/* O lado da conversa da vista da oferta: a projeção mora no kit
   (`hotelOfferViewOf`), e aqui ficam os textos do catálogo e os fatos que saem da
   régua do wire — a distância e o deslocamento são os mesmos que o agente lê. */

const COPY_BY_LOCALE = new Map<string, HotelOfferViewCopy>();

/** Os textos da vista da oferta num idioma. Pura e memoizada por idioma: a lista
 *  inteira monta uma vez, não por card. */
function hotelOfferViewCopy(locale: string): HotelOfferViewCopy {
  const cached = COPY_BY_LOCALE.get(locale);
  if (cached) return cached;
  const t = (key: Parameters<typeof genUiT>[1], vars?: Record<string, string | number>) =>
    genUiT(locale, key, vars);
  const copy: HotelOfferViewCopy = {
    badges: {
      favorite: t('hotel.badge.favorite'),
      stayedBefore: t('hotel.badge.stayedBefore'),
      stayedBeforeCount: (count) => t('hotel.badge.stayedBeforeCount', { count }),
      recommended: t('hotel.badge.recommended'),
      cheapest: t('hotel.badge.cheapest'),
      closest: t('hotel.badge.closest'),
      cheapestOnCard: t('hotel.badge.cheapestOnCard'),
      closestOnCard: t('hotel.badge.closestOnCard'),
      agentPick: t('hotel.badge.agentPick'),
      agreedRate: t('hotel.badge.agreedRate'),
    },
    amenity: (code) => t(`hotel.amenity.${code}`),
    decimal: (value) =>
      value.toLocaleString(resolveGenUiLocale(locale), {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    rating: (value) => t('hotel.rating', { rating: value }),
    distance: (km) => t('hotel.card.distance', { km }),
    travelTime: ({ minutes, mode }) =>
      `${t('hotel.card.minutes', { count: minutes })} ${t(
        mode === 'foot' ? 'hotel.card.byFoot' : 'hotel.card.byCar',
      )}`,
    money: fmtMoneyWhole,
    priceUnavailable: t('hotel.priceUnavailable'),
    perNight: t('hotel.card.perNight'),
    refundable: t('hotel.card.refundable'),
    refundableFrom: (price) => t('hotel.card.refundableFrom', { price }),
    outOfPolicy: t('hotel.outPolicy'),
  };
  COPY_BY_LOCALE.set(locale, copy);
  return copy;
}

/** Os fatos da oferta que saem da régua do wire. A régua é UMA
 *  (`shared/travel_estimate.ts`): a cena do agente de hotel lê a mesma e o
 *  retrato nunca diverge do que o card mostra. */
function hotelOfferFacts(
  offer: ChatUiHotelOffer,
  place: HotelPlannerPlace | undefined,
  locale: string,
): HotelOfferFacts {
  const km = hotelDistanceKm(offer, place);
  const reasons = hotelPolicyReasons(offer.policyViolations, offer.policyLimits, locale);
  return {
    ...(km === undefined ? {} : { distanceKm: km, travel: hotelTravelEstimate(km) }),
    ...(reasons.length > 0 ? { outOfPolicyReasons: reasons } : {}),
  };
}

export function hotelAmenityViews(
  amenities: readonly ChatUiHotelAmenity[],
  locale: string,
): HotelAmenityView[] {
  return hotelAmenityViewsOf(amenities, hotelOfferViewCopy(locale));
}

export function hotelTravelLabels(
  offer: ChatUiHotelOffer,
  place: HotelPlannerPlace | undefined,
  locale: string,
): Pick<HotelOfferView, 'distanceLabel' | 'travelTimeLabel'> {
  return hotelTravelLabelsOf(hotelOfferFacts(offer, place, locale), hotelOfferViewCopy(locale));
}

/** O que o card precisa saber ALÉM da oferta — ver `HotelOfferViewContext` no
 *  kit. Aqui chega a âncora do mapa (`placeDetails`), de onde saem a distância e
 *  o deslocamento. */
export type HotelOfferViewContext = {
  locale: string;
  proxyImageUrl: (url: string) => string;
  detailsAvailable: boolean;
  nights?: number;
  placeDetails?: HotelPlannerPlace;
  axis?: HotelAxis;
  axisOnCard?: boolean;
  highlighted?: boolean;
};

export function hotelOfferView(
  offer: ChatUiHotelOffer,
  context: HotelOfferViewContext,
): HotelOfferView {
  const { locale, placeDetails, ...rest } = context;
  return hotelOfferViewOf(offer, {
    ...rest,
    copy: hotelOfferViewCopy(locale),
    facts: hotelOfferFacts(offer, placeDetails, locale),
  });
}

/** As vistas de uma lista que NÃO veio do bloco — a página do painel. */
export function hotelOfferViews(
  offers: readonly ChatUiHotelOffer[],
  options: {
    locale: string;
    proxyImageUrl?: (url: string) => string;
    detailsAvailable?: boolean;
    nights?: number;
    place?: HotelPlannerPlace;
  },
): HotelOfferView[] {
  const { locale, place, ...rest } = options;
  return hotelOfferViewsOf(offers, {
    ...rest,
    copy: hotelOfferViewCopy(locale),
    factsOf: (offer) => hotelOfferFacts(offer, place, locale),
  });
}
